import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Check, Zap, Shield, Rocket, Users, MessageCircle, Euro, Loader2 } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';

const PROFILE_STYLES = {
  base: { icon: Shield, color: '#3ECF8E', gradient: 'from-emerald-50 to-green-50', border: 'border-emerald-200', tag: 'Risparmio' },
  moderato: { icon: Zap, color: '#2196F3', gradient: 'from-blue-50 to-sky-50', border: 'border-blue-200', tag: 'Consigliato' },
  aggressivo: { icon: Rocket, color: '#FF9800', gradient: 'from-orange-50 to-amber-50', border: 'border-orange-200', tag: 'Max Engagement' },
};

const AUTOMATION_LABELS = {
  welcome_message: 'Benvenuto',
  purchase_notification: 'Notifica punti',
  redeem_notification: 'Riscatto premi',
  referral_complete_notification: 'Referral completato',
  gift_points_notification: 'Regalo punti',
  challenge_completed_notification: 'Challenge completata',
  birthday: 'Compleanno',
  welcome_followup: 'Follow-up benvenuto',
  inactive: 'Clienti inattivi',
  reward_reminder: 'Promemoria premi',
  tier_reminder: 'Promemoria tier',
  unclaimed_reward: 'Premi non ritirati',
  tier_benefit_reminder: 'Benefici tier',
  second_visit_month: '2a visita del mese',
  tenth_visit_year: '10a visita anno',
  review_request: 'Richiesta recensione',
  expiry_warning: 'Scadenza punti',
};

export default function AdminMarketing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/marketing/setup')
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento profili marketing'))
      .finally(() => setLoading(false));
  }, []);

  const applyProfile = async (profileId) => {
    setApplying(profileId);
    try {
      await adminApi.post('/admin/marketing/apply', { profile_id: profileId });
      setData(d => ({ ...d, current_profile: profileId }));
      toast.success(`Profilo "${profileId}" applicato con successo!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setApplying(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Marketing Setup</h1>
        <p className="text-[#64748B] text-sm mt-1">Scegli il livello di comunicazione marketing verso i tuoi clienti</p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Clienti totali</span></div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data?.total_customers || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><MessageCircle size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Con WhatsApp</span></div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data?.wa_customers || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 mb-1"><Shield size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Profilo attivo</span></div>
          <p className="text-2xl font-bold capitalize" style={{ color: data?.current_profile ? PROFILE_STYLES[data.current_profile]?.color : '#888' }}>
            {data?.current_profile || 'Nessuno'}
          </p>
        </div>
      </div>

      {/* Profile cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data?.profiles?.map(profile => {
          const style = PROFILE_STYLES[profile.id];
          const Icon = style.icon;
          const isActive = data.current_profile === profile.id;
          const isApplying = applying === profile.id;

          return (
            <div key={profile.id}
              data-testid={`marketing-profile-${profile.id}`}
              className={`bg-gradient-to-br ${style.gradient} rounded-2xl border-2 ${isActive ? 'ring-2 ring-offset-2' : ''} shadow-sm transition-all hover:shadow-md relative overflow-hidden`}
              style={{ borderColor: isActive ? style.color : undefined, ringColor: isActive ? style.color : undefined }}>

              {/* Tag */}
              <div className="absolute top-3 right-3">
                <PageTooltip page="marketing" />
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: style.color }}>
                  {style.tag}
                </span>
              </div>

              {isActive && (
                <div className="absolute top-3 left-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white text-[#1A1A1A] shadow-sm flex items-center gap-1">
                    <Check size={10} /> Attivo
                  </span>
                </div>
              )}

              <div className="p-5 pt-10">
                {/* Icon + Name */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${style.color}20` }}>
                    <Icon size={24} style={{ color: style.color }} />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.name}</p>
                    <p className="text-xs text-[#64748B]">{profile.subtitle}</p>
                  </div>
                </div>

                <p className="text-sm text-[#555] mb-4 leading-relaxed">{profile.description}</p>

                {/* Key metrics */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white/70 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.automation_count}</p>
                    <p className="text-[10px] text-[#64748B]">Automazioni</p>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.broadcast_limit}</p>
                    <p className="text-[10px] text-[#64748B]">Broadcast/mese</p>
                  </div>
                </div>

                {/* Cost estimate */}
                <div className="bg-white/80 rounded-xl p-3 mb-4 border border-white">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Euro size={14} className="text-[#64748B]" />
                    <span className="text-xs font-semibold text-[#64748B]">Stima costi mensili</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{profile.costs.total_cost.toFixed(2)} <span className="text-sm font-normal text-[#64748B]">EUR/mese</span></p>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-[#64748B]">Transazionali ({profile.costs.transactional_msgs} msg)</span>
                      <span className="text-[#555] font-medium">{profile.costs.transactional_cost.toFixed(2)} EUR</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-[#64748B]">Marketing auto ({profile.costs.marketing_auto_msgs} msg)</span>
                      <span className="text-[#555] font-medium">{profile.costs.marketing_cost.toFixed(2)} EUR</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-[#64748B]">Broadcast ({profile.costs.broadcast_msgs} msg)</span>
                      <span className="text-[#555] font-medium">{profile.costs.broadcast_cost.toFixed(2)} EUR</span>
                    </div>
                  </div>
                </div>

                {/* Automations list */}
                <details className="mb-4 group">
                  <summary className="text-xs font-semibold text-[#64748B] cursor-pointer hover:text-[#555] transition-colors">
                    Automazioni incluse ({profile.automation_count})
                  </summary>
                  <div className="mt-2 space-y-1 pl-1">
                    {profile.automations.map(a => (
                      <div key={a} className="flex items-center gap-1.5 text-[11px]">
                        <Check size={10} style={{ color: style.color }} />
                        <span className="text-[#555]">{AUTOMATION_LABELS[a] || a}</span>
                      </div>
                    ))}
                  </div>
                </details>

                {/* Apply button */}
                <button
                  onClick={() => applyProfile(profile.id)}
                  disabled={isActive || isApplying}
                  data-testid={`apply-profile-${profile.id}`}
                  className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: isActive ? '#E5E5E5' : style.color, color: isActive ? '#888' : '#fff' }}>
                  {isApplying ? <><Loader2 size={16} className="animate-spin" /> Applicando...</> :
                   isActive ? <><Check size={16} /> Profilo attivo</> : 'Applica profilo'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info box */}
      <div className="mt-6 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <p className="text-sm font-semibold text-[#1A1A1A] mb-2">Come funziona</p>
        <ul className="text-xs text-[#64748B] space-y-1.5">
          <li>Scegliendo un profilo, le automazioni incluse verranno attivate automaticamente nella sezione Automazioni</li>
          <li>Il limite broadcast mensile viene aggiornato in base al profilo scelto</li>
          <li>I costi sono stime basate sui {data?.wa_customers || 0} clienti con consenso WhatsApp attivo</li>
          <li>Puoi personalizzare ulteriormente le singole automazioni dalla pagina Automazioni</li>
          <li>Cambiare profilo non influenza i broadcast già inviati</li>
        </ul>
      </div>
    </div>
  );
}
